/**
 * Canonical month/year anchor for an invoice: the period END (date_to), falling
 * back to date_from for legacy rows that predate date_to. Returns 'YYYY-MM-DD'
 * or null. EVERY surface that buckets/filters invoices by month/year must use
 * this so the list, dashboard, and grouping agree (getInvoices fetches by period
 * overlap and already returns the row).
 */
export function invoicePeriodAnchorIso(invoice) {
  return invoice?.date_to || invoice?.date_from || null;
}

/**
 * The YEAR an invoice belongs to — the year of its period-END anchor. Returns a
 * number, or null when the invoice has no usable date.
 *
 * This is the ONE definition. Before it existed, three surfaces disagreed: the
 * year picker derived years from `date_from` (period START), the DB fetch used
 * period OVERLAP, and the list/grouping used this anchor. An invoice spanning a
 * year boundary (Dec 2025 → Jan 2026) therefore offered a "2025" option in the
 * picker that could never render a row — the list correctly filed it under 2026.
 * Anything answering "which year is this invoice" must call this.
 *
 * Note the FETCH is deliberately still an overlap query (`getInvoices({ year })`
 * uses `date_from <= YYYY-12-31 AND date_to >= YYYY-01-01`) — it must over-fetch
 * so Ready-to-Bill can see boundary-spanning invoices. Over-fetching is correct;
 * the bug was letting an over-fetched row drive the year OPTIONS.
 */
export function invoiceAnchorYear(invoice) {
  const iso = invoicePeriodAnchorIso(invoice);
  if (!iso) return null;
  const year = new Date(`${String(iso).slice(0, 10)}T00:00:00`).getFullYear();
  return Number.isNaN(year) ? null : year;
}
